import { initKewaTracker, sendPageview, setKewaContact, trackEvent } from 'kewa-tracker-web';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { KEWA_CONFIG, KEWA_EVENTS } from '../config/kewa.config';
import { useAccount } from './useAccount';

/**
 * Custom hook for Kewa Analytics integration
 * Automatically includes user context and app metadata
 */
export const useKewa = () => {
  const account = useAccount();

  const location = useLocation();
  const isInitialized = useRef(false);
  const lastPageview = useRef(null);

  // Build contact information from account data
  const contactInfo = useMemo(
    () => ({
      userid: account.profile?.id,
      customerid: account.profile?.customerId,
      email: account?.user?.email,
      firstname: account?.user?.firstName,
      lasname: account?.user?.lastName,
      fullname: account?.user?.name,
      name: account?.user?.name,
      role: JSON.stringify(account?.user?.authorities),
      businessvertical: JSON.stringify(account?.user?.businessVerticals),
      company: account?.user?.profile?.businessName || 'N/A',
      financialyearstartdate: account?.selectedFinancialYear?.fromDate || 'N/A',
      financialyearenddate: account?.selectedFinancialYear?.toDate || 'N/A',
      fulfillmentyearstartdate: account?.selectedFulfilmentYear?.fromDate || 'N/A',
      fulfillmentyearenddate: account?.selectedFulfilmentYear?.toDate || 'N/A',
      isusingfulfilment: account.isUsingFulfilment,
      appcode: KEWA_CONFIG.appCode,
      appversion: KEWA_CONFIG.appVersion,
      environment: KEWA_CONFIG.environment,
      timestamp: new Date().toISOString(),
    }),
    [account]
  );

  useEffect(() => {
    const initializeKewa = async () => {
      // Prevent multiple initialization attempts
      if (isInitialized.current) {
        return;
      }

      isInitialized.current = true;

      if (!KEWA_CONFIG.enabled) {
        console.log('Kewa Analytics is disabled');
        return;
      }

      if (!KEWA_CONFIG.baseUrl || !KEWA_CONFIG.projectId) {
        console.error('Kewa Analytics: baseUrl is not configured');
        return;
      }

      try { 

        await initKewaTracker({
          baseUrl: KEWA_CONFIG.baseUrl,
          projectId: KEWA_CONFIG.projectId,
          customPageviewTracking: KEWA_CONFIG.customPageviewTracking,
        });

        isInitialized.current = true;

        // Set contact information after initialization
        if (account?.user?.email) {
          try {
            await setKewaContact(contactInfo);
          } catch (contactError) {
            console.error('Failed to set Kewa contact:', contactError);
          }
        }
       
      } catch (error) {
        console.error(' Failed to initialize Kewa Analytics:', error);
        isInitialized.current = false;
      }
    };

    // Initialize immediately, don't wait for authentication


      initializeKewa();
    
  }, [account?.isAuthenticate]);

  // Auto pageview tracking
  useEffect(() => {
    const currentPath = location.pathname;

    // Avoid duplicate pageviews
    if (lastPageview.current === currentPath || !isInitialized.current) return;
    const title = document.title?.split('-')?.[0]?.trim() || 'Untitled';
    const sendAutoPageview = async () => {
      try {
        await sendPageview({
          url: currentPath,
          title: title,
          search: location.search,
          referrer: lastPageview.current,
          ...contactInfo,
        });
        lastPageview.current = currentPath;
      } catch (error) {
        console.error('Failed to send pageview:', error);
      }
    };

    const trackPageChangeEvent = async () => {
      const title = document.title?.split('-')?.[0]?.trim() || 'Untitled';

      try {
        await trackEvent(
          KEWA_EVENTS.PAGE_VIEW,
          {
            url: currentPath,
            title: title,
            search: location.search,
            referrer: lastPageview.current,
            email: contactInfo?.email,
            name: contactInfo?.name,
          },
          contactInfo,
          new Date().toISOString()
        );
      } catch (error) {
        console.error('Failed to track page change event:', error);
      }
    };

    if (account?.isAuthenticate) {
      sendAutoPageview();
      trackPageChangeEvent();
    }
  }, [location.pathname, location.search, contactInfo, account?.isAuthenticate]);

  // Custom pageview tracking
  const trackPageview = useCallback(
    async (pageData = {}) => {
      if (!isInitialized.current) {
        console.warn('Kewa Analytics not initialized');
        return;
      }

      try {
        await sendPageview({
          url: location.pathname + location.search,
          title: document.title?.split('-')[0]?.trim() || 'Untitled',
          ...pageData,
          ...contactInfo,
        });
      } catch (error) {
        console.error('Failed to track pageview:', error);
      }
    },
    [location, contactInfo]
  );

  // Custom event tracking
  const trackAnalyticsEvent = useCallback(
    async (eventName, eventData = {}, customContact = {}, customTimestamp = null) => {
      if (!isInitialized.current) {
        console.warn('Kewa Analytics not initialized');
        return;
      }

      try {
        const finalContact = {
          ...contactInfo,
          ...customContact,
        };

        const finalEventData = {
          page: location.pathname,
          url: location.pathname + location.search,
          email: contactInfo?.email,
          name: contactInfo?.name,
          ...eventData,
        };

        await trackEvent(
          eventName,
          finalEventData,
          finalContact,
          customTimestamp || new Date().toISOString()
        );
      } catch (error) {
        console.error('Failed to track event:', error);
      }
    },
    [contactInfo, location]
  );

  // Predefined tracking methods
  const trackUserLogin = useCallback(
    (loginMethod = 'email', additionalData = {}) => {
      return trackAnalyticsEvent(KEWA_EVENTS.USER_LOGIN, {
        login_method: loginMethod,
        login_timestamp: new Date().toISOString(),
        ...additionalData,
      });
    },
    [trackAnalyticsEvent]
  );

  const trackUserLogout = useCallback(
    (additionalData = {}) => {
      return trackAnalyticsEvent(KEWA_EVENTS.USER_LOGOUT, {
        logout_timestamp: new Date().toISOString(),
        session_duration: sessionStorage.getItem('session_start')
          ? Date.now() - parseInt(sessionStorage.getItem('session_start'))
          : null,
        ...additionalData,
      });
    },
    [trackAnalyticsEvent]
  );

  // Specific tracking methods for common events
  const trackButtonClick = useCallback(
    (buttonId, additionalData = {}) => {
      return trackAnalyticsEvent(KEWA_EVENTS.BUTTON_CLICK, {
        button_id: buttonId,
        ...additionalData,
      });
    },
    [trackAnalyticsEvent]
  );

  const trackFormSubmit = useCallback(
    (formName, formData = {}) => {
      return trackAnalyticsEvent(KEWA_EVENTS.FORM_SUBMIT, {
        form_name: formName,
        form_data: formData,
      });
    },
    [trackAnalyticsEvent]
  );

  const trackDownload = useCallback(
    (fileName, fileType, source) => {
      return trackAnalyticsEvent(KEWA_EVENTS.DOWNLOAD, {
        file_name: fileName,
        file_type: fileType,
        source: source,
      });
    },
    [trackAnalyticsEvent]
  );

  const trackError = useCallback(
    (errorType, errorMessage, errorStack = null) => {
      return trackAnalyticsEvent(KEWA_EVENTS.ERROR, {
        error_type: errorType,
        error_message: errorMessage,
        error_stack: errorStack,
        user_agent: navigator.userAgent,
        email: contactInfo?.email,
        name: contactInfo?.name,
      });
    },
    [trackAnalyticsEvent]
  );

  // Reset contact information
  const resetContact = useCallback(async () => {
    try {
      // await resetKewaContact();
    } catch (error) {
      console.error('Failed to reset Kewa contact:', error);
    }
  }, []);

  return {
    // Core methods
    trackPageview,
    trackEvent: trackAnalyticsEvent,

    // Common event methods
    trackButtonClick,
    trackFormSubmit,
    trackDownload,
    trackError,
    trackUserLogin,
    trackUserLogout,

    // Contact management
    // resetKewaContact: resetContact,

    // Utility
    contactInfo,
    isInitialized: isInitialized.current,

    // Constants
    EVENTS: KEWA_EVENTS,
  };
};
